import { useEffect, useRef, useState } from 'react';

// ===== TUNING KNOBS =====
const GLOW_SIZE = 320;              // Blob diameter in px (260-360 range)
const GLOW_OPACITY = 0.85;          // Overall blob opacity
const LERP_FACTOR = 0.12;           // Smoothing factor (lower = smoother)
const IDLE_TIMEOUT = 150;           // ms before stopping rAF loop
// ========================

const AmbientBackground = () => {
  const blobRef = useRef<HTMLDivElement>(null);
  const rafRef = useRef<number | null>(null);
  const targetRef = useRef({ x: 0, y: 0 });
  const currentRef = useRef({ x: 0, y: 0 });
  const lastMoveRef = useRef(0);
  const isAnimatingRef = useRef(false);
  const [isDisabled, setIsDisabled] = useState(false);

  useEffect(() => {
    // Check for reduced motion or touch devices
    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const isCoarsePointer = window.matchMedia('(pointer: coarse)').matches;
    
    if (prefersReducedMotion || isCoarsePointer) {
      setIsDisabled(true);
      return;
    }

    // Initialize position to center of viewport
    const centerX = window.innerWidth / 2 - GLOW_SIZE / 2;
    const centerY = window.innerHeight / 2 - GLOW_SIZE / 2;
    targetRef.current = { x: centerX, y: centerY };
    currentRef.current = { x: centerX, y: centerY };

    const animate = () => {
      // Lerp toward target
      currentRef.current.x += (targetRef.current.x - currentRef.current.x) * LERP_FACTOR;
      currentRef.current.y += (targetRef.current.y - currentRef.current.y) * LERP_FACTOR;

      // Apply transform
      if (blobRef.current) {
        blobRef.current.style.transform = `translate3d(${currentRef.current.x}px, ${currentRef.current.y}px, 0)`;
      }

      // Check if we should stop animating (idle for IDLE_TIMEOUT ms)
      if (Date.now() - lastMoveRef.current > IDLE_TIMEOUT) {
        isAnimatingRef.current = false;
        rafRef.current = null;
        return;
      }

      rafRef.current = requestAnimationFrame(animate);
    };

    const startAnimation = () => {
      if (!isAnimatingRef.current) {
        isAnimatingRef.current = true;
        rafRef.current = requestAnimationFrame(animate);
      }
    };

    const handlePointerMove = (e: PointerEvent) => {
      // Update target position (centered on cursor)
      targetRef.current = {
        x: e.clientX - GLOW_SIZE / 2,
        y: e.clientY - GLOW_SIZE / 2,
      };
      lastMoveRef.current = Date.now();
      startAnimation();
    };

    window.addEventListener('pointermove', handlePointerMove, { passive: true });

    return () => {
      window.removeEventListener('pointermove', handlePointerMove);
      if (rafRef.current) {
        cancelAnimationFrame(rafRef.current);
      }
    };
  }, []);

  // Don't render anything on touch devices or reduced motion
  if (isDisabled) {
    return null;
  }

  return (
    <div
      ref={blobRef}
      className="fixed top-0 left-0 pointer-events-none select-none"
      style={{
        width: GLOW_SIZE,
        height: GLOW_SIZE,
        borderRadius: '50%',
        background: `radial-gradient(circle,
          rgba(34, 197, 94, 0.22) 0%,
          rgba(34, 197, 94, 0.10) 30%,
          rgba(34, 197, 94, 0) 65%
        )`,
        opacity: GLOW_OPACITY,
        willChange: 'transform',
        zIndex: 0,
      }}
      aria-hidden="true"
    />
  );
};

export default AmbientBackground;
